import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
  CognitoUserSession,
  AuthenticationDetails,
} from "amazon-cognito-identity-js";
import { config } from "../config";

type AuthUser = {
  email: string;
  displayName: string;
  idToken: string;
  accessToken: string;
};

type AuthCtx = {
  user: AuthUser | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, displayName: string) => Promise<void>;
  confirm: (email: string, code: string) => Promise<void>;
  signOut: () => void;
  getIdToken: () => Promise<string | null>;
};

const Ctx = createContext<AuthCtx | null>(null);
export const useAuth = () => {
  const c = useContext(Ctx);
  if (!c) throw new Error("useAuth outside provider");
  return c;
};

const pool = new CognitoUserPool({
  UserPoolId: config.cognitoUserPoolId,
  ClientId: config.cognitoClientId,
});

function sessionToUser(session: CognitoUserSession): AuthUser {
  const idPayload = session.getIdToken().payload as Record<string, string>;
  return {
    email: idPayload.email ?? "",
    displayName: idPayload["custom:display_name"] ?? idPayload.email ?? "",
    idToken: session.getIdToken().getJwtToken(),
    accessToken: session.getAccessToken().getJwtToken(),
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const current = pool.getCurrentUser();
    if (!current) {
      setLoading(false);
      return;
    }
    current.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (!err && session?.isValid()) setUser(sessionToUser(session));
      setLoading(false);
    });
  }, []);

  const signIn = useCallback((email: string, password: string) => {
    return new Promise<void>((resolve, reject) => {
      const cognitoUser = new CognitoUser({ Username: email, Pool: pool });
      const auth = new AuthenticationDetails({ Username: email, Password: password });
      cognitoUser.authenticateUser(auth, {
        onSuccess: (session) => {
          setUser(sessionToUser(session));
          resolve();
        },
        onFailure: (e) => reject(e),
      });
    });
  }, []);

  const signUp = useCallback((email: string, password: string, displayName: string) => {
    return new Promise<void>((resolve, reject) => {
      const attrs = [
        new CognitoUserAttribute({ Name: "email", Value: email }),
        new CognitoUserAttribute({ Name: "custom:display_name", Value: displayName }),
      ];
      pool.signUp(email, password, attrs, [], (err) => {
        if (err) reject(err);
        else resolve();
      });
    });
  }, []);

  const confirm = useCallback((email: string, code: string) => {
    return new Promise<void>((resolve, reject) => {
      const cognitoUser = new CognitoUser({ Username: email, Pool: pool });
      cognitoUser.confirmRegistration(code, true, (err) => {
        if (err) reject(err);
        else resolve();
      });
    });
  }, []);

  const signOut = useCallback(() => {
    pool.getCurrentUser()?.signOut();
    setUser(null);
  }, []);

  const getIdToken = useCallback(async () => {
    const current = pool.getCurrentUser();
    if (!current) return null;
    return new Promise<string | null>((resolve) => {
      current.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session?.isValid()) resolve(null);
        else resolve(session.getIdToken().getJwtToken());
      });
    });
  }, []);

  const value = useMemo<AuthCtx>(
    () => ({ user, loading, signIn, signUp, confirm, signOut, getIdToken }),
    [user, loading, signIn, signUp, confirm, signOut, getIdToken]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
